/**
 * SidebarLayout Component
 * 
 * Main sidebar layout component for admin dashboard.
 * Contains navigation header, navigation items, and user info.
 * Supports role-based navigation filtering and mobile responsiveness.
 * 
 * @component
 * @example
 * <SidebarLayout 
 *   user={adminUser} 
 *   currentRoute="/dashboard/customers" 
 *   onNavigate={handleNavigate} 
 * />
 */

import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../../../common/Sidebar';
import { AdminUser, NavigationItem as NavigationItemType } from '../../../../types/admin-v2';
import { getNavigationItemsForRole } from '../../../../config/admin-v2-permissions';
import { API_ENDPOINTS } from '../../../../utils/config';
import './SidebarLayout.scss';

interface SidebarLayoutProps {
  /** Current admin user */
  user: AdminUser;
  /** Current active route */
  currentRoute: string;
  /** Navigation callback */
  onNavigate: (route: string) => void;
  /** Whether sidebar is collapsed on mobile */
  isCollapsed?: boolean;
  /** Toggle sidebar callback */
  onToggleCollapse?: () => void;
  /** Logout callback */
  onLogout?: () => void;
  /** Custom class name */
  className?: string;
  /** Secondary sidebar component */
  secondarySidebar?: React.ReactNode;
  /** Dynamic sub-items function - returns sub-items for a parent item based on current route */
  getDynamicSubItems?: (parentId: string, currentRoute: string, permissions?: any, userRole?: string, userCompanyPlan?: string) => Array<{
    id: string;
    label: string;
    route: string;
    icon?: string;
  }> | undefined;
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  user,
  currentRoute,
  onNavigate,
  isCollapsed = false,
  onToggleCollapse,
  onLogout,
  className = '',
  secondarySidebar,
  getDynamicSubItems
}) => {
  const [filteredNavigationItems, setFilteredNavigationItems] = useState<NavigationItemType[]>([]);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [hasMentions, setHasMentions] = useState(false);

  // Filter navigation items based on user role
  useEffect(() => {
    const items = getNavigationItemsForRole(user.role);
    setFilteredNavigationItems(items);
  }, [user.role]);

  // Fetch inbox unread count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      try {
        const response = await fetch(API_ENDPOINTS.USER_INBOX_UNREAD_COUNT, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const count = data.unread_count || 0;
            const mentions = data.has_mentions || false;
            console.log('📬 Admin Inbox unread count:', count, 'Has mentions:', mentions);
            setInboxUnreadCount(count);
            setHasMentions(mentions);
          } else {
            console.error('❌ Failed to fetch unread count:', data.error);
          }
        } else {
          console.error('❌ Failed to fetch unread count, status:', response.status);
        }
      } catch (error) {
        console.error('❌ Error fetching inbox unread count:', error);
      }
    };

    fetchUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    // Listen for inbox update events
    const handleInboxUpdate = () => {
      console.log('📬 Admin Inbox update event received, refreshing unread count...');
      fetchUnreadCount();
    };

    window.addEventListener('inbox-updated', handleInboxUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('inbox-updated', handleInboxUpdate);
    };
  }, []);

  // Convert admin navigation items to generic sidebar format with badge
  const navItems = useMemo(() => {
    const mapped = filteredNavigationItems.map(item => {
      const baseItem = {
        id: item.id,
        label: item.label,
        icon: item.icon || '●', // Default icon if undefined
        route: item.route,
        badge: item.id === 'user-inbox' && inboxUnreadCount > 0 ? inboxUnreadCount : undefined,
        badgeVariant: (item.id === 'user-inbox' && hasMentions ? ('mentioned' as const) : undefined),
        children: item.children?.map(child => ({
          id: child.id,
          label: child.label,
          icon: child.icon || '●', // Default icon if undefined
          route: child.route
        }))
      };

      // Add dynamic sub-items if function is provided
      // Add sub-items for special items (RFQs and Customer List with dynamic subitems)
      if (getDynamicSubItems && (item.id === 'open-rfqs' || item.id === 'customer-list')) {
        // Pass user context to getDynamicSubItems for permission checks
        // user.company might be a string ID or object, try to access plan safely
        const companyPlan = typeof user.company === 'object' ? user.company?.plan : undefined;

        const dynamicSubItems = getDynamicSubItems(
          item.id,
          currentRoute,
          user.permissions,
          user.role,
          companyPlan
        );
        if (dynamicSubItems && dynamicSubItems.length > 0) {
          // Merge dynamic items AFTER existing children (Pro, Plus, Basic)
          // This way plan filters stay at the top, RFQ nav items appear below
          baseItem.children = [
            ...(baseItem.children || []),
            ...dynamicSubItems.map(subItem => ({
              id: subItem.id,
              label: subItem.label,
              icon: subItem.icon || '●',
              route: subItem.route
            }))
          ];
        }
      }

      return baseItem;
    });

    // Debug: Log open-rfqs item
    const openRfqsItem = mapped.find(item => item.id === 'open-rfqs');
    if (openRfqsItem) {
      console.log('🔍 Open RFQs item:', openRfqsItem);
      console.log('🔍 Open RFQs children:', openRfqsItem.children);
      console.log('🔍 Open RFQs children count:', openRfqsItem.children?.length || 0);
      console.log('🔍 Current route:', currentRoute);
      if (getDynamicSubItems) {
        const dynamicItems = getDynamicSubItems('open-rfqs', currentRoute);
        console.log('🔍 Dynamic sub-items:', dynamicItems);
      }
    }

    return mapped;
  }, [filteredNavigationItems, inboxUnreadCount, hasMentions, currentRoute, getDynamicSubItems]);

  return (
    <Sidebar
      logo="GA"
      navItems={navItems}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      user={{
        name: user.name,
        email: user.email
      }}
      onLogout={onLogout || (() => { })}
      variant="admin"
    />
  );
};

export default SidebarLayout;
